import { FC, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Grid, TextInput } from 'portal-commons';
import { RcsCampaign } from '../../types';
import {
  createMaxLengthValidator,
  createUrlValidator,
  isRequired,
  isValidInput,
} from '@/applications/CspPortal/Campaigns/validators';

interface LaunchDetailsFormProps {
  campaign: RcsCampaign;
}

interface LaunchDetailsFormValues {
  actionTriggerMessages: string;
  messageTypes: string;
  optinFlow: string;
  optoutFlow: string;
  screenshotUrl: string;
  videoUrl: string;
}

const LaunchDetailsForm: FC<LaunchDetailsFormProps> = ({ campaign }) => {
  const { control, clearErrors, reset } =
    useFormContext<LaunchDetailsFormValues>();

  const defaultValues = useMemo(() => {
    return {
      actionTriggerMessages: campaign.actionTriggerMessages || '',
      messageTypes: campaign.messageTypes || '',
      optinFlow: campaign.optinFlow || '',
      optoutFlow: campaign.optoutFlow || '',
      screenshotUrl: campaign.screenshotUrl || '',
      videoUrl: campaign.videoUrl || '',
    };
  }, [campaign]);

  const {
    field: { value: actionTriggerMessages, onChange: setActionTriggerMessages },
    fieldState: { error: actionTriggerMessagesError },
  } = useController({
    control,
    name: 'actionTriggerMessages',
    defaultValue: campaign.actionTriggerMessages || '',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2000),
      },
    },
  });

  const {
    field: { value: messageTypes, onChange: setMessageTypes },
    fieldState: { error: messageTypesError },
  } = useController({
    control,
    name: 'messageTypes',
    defaultValue: campaign.messageTypes || '',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2000),
      },
    },
  });

  const {
    field: { value: optinFlow, onChange: setOptinFlow },
    fieldState: { error: optinFlowError },
  } = useController({
    control,
    name: 'optinFlow',
    defaultValue: campaign.optinFlow || '',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2000),
      },
    },
  });

  const {
    field: { value: optoutFlow, onChange: setOptoutFlow },
    fieldState: { error: optoutFlowError },
  } = useController({
    control,
    name: 'optoutFlow',
    defaultValue: campaign.optoutFlow || '',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2000),
      },
    },
  });

  const {
    field: { value: screenshotUrl, onChange: setScreenshotUrl },
    fieldState: { error: screenshotUrlError },
  } = useController({
    control,
    name: 'screenshotUrl',
    defaultValue: campaign.screenshotUrl || '',
    rules: {
      validate: {
        isValidInput,
        withValidInput: createUrlValidator(),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: { value: videoUrl, onChange: setVideoUrl },
    fieldState: { error: videoUrlError },
  } = useController({
    control,
    name: 'videoUrl',
    defaultValue: campaign.videoUrl || '',
    rules: {
      validate: {
        isValidInput,
        withValidInput: createUrlValidator(),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  useEffect(() => {
    clearErrors();
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Grid
      sx={{
        gridTemplateColumns: '1fr 1fr',
        columnGap: 'l',
        rowGap: 'xl',
      }}
    >
      <TextInput
        label="What actions will trigger messages?"
        value={actionTriggerMessages}
        onChange={setActionTriggerMessages}
        error={!!actionTriggerMessagesError?.message}
        note={actionTriggerMessagesError?.message}
        data-testid="actionTriggerMessages"
      />
      <TextInput
        label="Types of Messages Sent"
        value={messageTypes}
        onChange={setMessageTypes}
        error={!!messageTypesError?.message}
        note={messageTypesError?.message}
        data-testid="messageTypes"
      />
      <TextInput
        label="Opt-In"
        value={optinFlow}
        onChange={setOptinFlow}
        error={!!optinFlowError?.message}
        note={optinFlowError?.message}
        data-testid="optinFlow"
      />
      <TextInput
        label="Opt-Out"
        value={optoutFlow}
        onChange={setOptoutFlow}
        error={!!optoutFlowError?.message}
        note={optoutFlowError?.message}
        data-testid="optoutFlow"
      />
      <TextInput
        label="Screenshot URL"
        value={screenshotUrl}
        onChange={setScreenshotUrl}
        error={!!screenshotUrlError?.message}
        note={screenshotUrlError?.message}
        data-testid="screenshotUrl"
      />
      <TextInput
        label="Video URL"
        value={videoUrl}
        onChange={setVideoUrl}
        error={!!videoUrlError?.message}
        note={videoUrlError?.message}
        data-testid="videoUrl"
      />
    </Grid>
  );
};

export default LaunchDetailsForm;
